import { useApolloClient, useMutation } from '@apollo/client';
import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';
import { usePlaidLink, type PlaidLinkOnSuccessMetadata, type PlaidLinkOptions } from 'react-plaid-link';

import { PlaidLinkTokenItemOutcome, PlaidLinkTokenStatus } from '../../__generated__/globalTypes';
import { useFinancialAccounts } from '../../hooks/queries/useFinancialAccounts';
import { useFeatureGate } from '../../hooks/useFeatureGates';
import {
  incrementPeopleProperty,
  trackAccountConnected,
  trackPlaidLinkExited,
  trackPlaidLinkOpened,
} from '../../lib/mixpanel';
import { extractErrorCode } from '../../utils/apollo/error';

import { PLAID_LINK_TOKEN, PLAID_LINK_TOKEN_UPDATED } from './plaidLinkToken';
import { CREATE_FINANCIAL_ACCOUNTS, CREATE_LINK_TOKEN, ARCHIVE_BANK_CONNECTION_WORK_ITEMS } from './plaidMutations';

import type {
  PlaidLinkTokenFieldsFragment,
  PlaidLinkTokenQuery,
  PlaidLinkTokenQueryVariables,
  PlaidLinkTokenUpdatedSubscription,
} from './__generated__/plaidLinkToken.gql';
import type {
  CreateFinancialAccountsMutation,
  CreateFinancialAccountsMutationVariables,
  CreateLinkTokenMutation,
  CreateLinkTokenMutationVariables,
} from './__generated__/plaidMutations.gql';

/** The financial accounts a successful link produced (new-connection or reconnection both return this shape). */
export type LinkedFinancialAccounts = CreateFinancialAccountsMutation['createFinancialAccounts'];

interface ILinkSessionWait {
  session: PlaidLinkTokenFieldsFragment | null;
  /** The wait ended on a failing read, so the outcome is unknown rather than absent. */
  pollFailed: boolean;
}

const LINK_SESSION_POLL_MS = 2_000;
// A multi-item session's outcome arrives over Plaid's webhook, not `onSuccess`;
// past this the flow reports it as still pending rather than keep spinning.
const LINK_SESSION_WAIT_MS = 90_000;
// An exit may follow banks the user did link before closing; a short look
// catches them without stalling a plain cancel behind a full wait.
const LINK_SESSION_EXIT_WAIT_MS = 8_000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface IUseAccountLinkOptions {
  /**
   * Skip the connection-selection modal and go straight to Plaid for an
   * existing connection (reconnection). `null` / a string both mean "skip the
   * modal"; `undefined` (the default) means "open the modal first". Matches the
   * `PlaidLinkButton` prop semantics.
   */
  bankConnectionId?: string | null;
  /** Called once the linked accounts are persisted (new connection or reconnection). */
  onLinked?: (accounts: LinkedFinancialAccounts, context: { isReconnection: boolean }) => void;
  /**
   * Called when the user backs out without linking — dismissing the
   * connection modal without a selection, or exiting the Plaid flow. Not called
   * on the error path (an error re-surfaces the modal so the user can retry).
   */
  onCancelled?: () => void;
}

export interface IUseAccountLink {
  /** Start the flow — open the selection modal, or jump straight to Plaid when `bankConnectionId` was given. */
  trigger: () => void;
  /** True when the flow skips the selection modal (a reconnection target was supplied). */
  shouldSkipModal: boolean;
  isModalOpen: boolean;
  onModalOpenChange: (isOpen: boolean) => void;
  onModalSelect: (bankConnectionId: string | undefined) => void;
  errorCode: ApiErrorCode | null;
  isLoading: boolean;
}

/**
 * The shared Plaid account-linking state machine: connection-selection modal →
 * link token → Plaid Link → wait for the session's banks (new connection) or
 * add-accounts (reconnection) → tracking. The single source of truth behind both the
 * onboarding/settings `PlaidLinkButton` and the assistant's
 * `financial_account_connect` flow, so duplicate handling, reconnection, and
 * analytics can't drift between the two entry points.
 *
 * A new-bank session is a multi-item one and hands nothing to `onSuccess`:
 * its banks land server-side off Plaid's webhook, which records what each
 * became. The hook waits on that record — the subscription for speed, the
 * query as the floor — and reports the new accounts, the existing duplicate
 * message, or an error; a session that linked some banks and failed others
 * reports both. `PLAID_LINK_PENDING` only when no record shows up in time and
 * the reads themselves were healthy.
 *
 * The hook owns the flow; the caller renders the `PlaidConnectionModal` from
 * the returned `{ isModalOpen, onModalOpenChange, onModalSelect, errorCode }`
 * and decides what triggers it (a button click vs. mounting). Outcomes surface
 * through `onLinked` / `onCancelled`; errors re-open the modal with a
 * `DUPLICATE_ITEM`-aware message rather than ending the flow.
 *
 * @param options.bankConnectionId Reconnection target. A string/`null` skips
 *   the selection modal and goes straight to Plaid for that connection;
 *   `undefined` (default) opens the modal first.
 * @param options.onLinked Called once the linked accounts are persisted, with
 *   the accounts and `{ isReconnection }`. Passed through a ref, so an inline
 *   arrow is fine.
 * @param options.onCancelled Called when the user backs out without linking
 *   (modal dismissed without a selection, or Plaid exited). Not called on the
 *   error path. Also ref-stable.
 * @returns The flow controls: `trigger` to start it, the
 *   `PlaidConnectionModal` props (`isModalOpen` / `onModalOpenChange` /
 *   `onModalSelect` / `errorCode`), `shouldSkipModal` (true when a reconnection
 *   target was supplied, so the caller surfaces errors inline instead of in the
 *   modal), and `isLoading` for a trigger control.
 */
export function useAccountLink({
  bankConnectionId,
  onLinked,
  onCancelled,
}: IUseAccountLinkOptions = {}): IUseAccountLink {
  const { financialAccounts, refetch: refetchFinancialAccounts } = useFinancialAccounts();
  const client = useApolloClient();
  const subscriptionsEnabled = useFeatureGate(FeatureGate.GraphqlSubscriptions);
  const financialAccountsRef = React.useRef(financialAccounts);
  financialAccountsRef.current = financialAccounts;
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedBankConnectionId, setSelectedBankConnectionId] = React.useState<string | null | undefined>(
    bankConnectionId
  );
  const [shouldTriggerPlaid, setShouldTriggerPlaid] = React.useState(false);
  const [linkToken, setLinkToken] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState<ApiErrorCode | null>(null);

  // bankConnectionId provided → skip the selection modal and reconnect directly.
  const shouldSkipModal = bankConnectionId !== undefined;

  // Read the outcome callbacks through refs so callers can pass inline arrows
  // without destabilizing the memoized handlers below (and so a callback can't
  // go stale across the async exchange).
  const onLinkedRef = React.useRef(onLinked);
  onLinkedRef.current = onLinked;
  const onCancelledRef = React.useRef(onCancelled);
  onCancelledRef.current = onCancelled;

  // Whether the open Link session is a new-bank one, which may connect several
  // banks and reports them over the webhook; a reconnection hands its accounts
  // to `onSuccess`. A ref, not state: Plaid captures the callbacks when the
  // handler is built, and this is read from inside them.
  const isMultiItemSessionRef = React.useRef(false);
  // The token the open session runs on. Its row is what the session's outcome
  // lands in, and the poll below reads it by this token.
  const linkTokenRef = React.useRef<string | null>(null);
  const isMountedRef = React.useRef(false);

  const reset = React.useCallback(() => {
    setLinkToken(null);
    setShouldTriggerPlaid(false);
    setSelectedBankConnectionId(undefined);
    setIsLoading(false);
    isMultiItemSessionRef.current = false;
    linkTokenRef.current = null;
  }, []);

  const openModalWithError = React.useCallback(
    (code: ApiErrorCode) => {
      setErrorCode(code);
      // When skipping the modal there's nowhere to surface a modal error — the
      // caller renders an inline message off `errorCode` instead.
      if (!shouldSkipModal) {
        setIsModalOpen(true);
      }
      reset();
    },
    [reset, shouldSkipModal]
  );

  const [createLinkToken] = useMutation<CreateLinkTokenMutation, CreateLinkTokenMutationVariables>(CREATE_LINK_TOKEN, {
    errorPolicy: 'all',
  });
  const [createFinancialAccounts] = useMutation<
    CreateFinancialAccountsMutation,
    CreateFinancialAccountsMutationVariables
  >(CREATE_FINANCIAL_ACCOUNTS, { errorPolicy: 'all' });
  const [archiveBankConnectionWorkItems] = useMutation(ARCHIVE_BANK_CONNECTION_WORK_ITEMS, { errorPolicy: 'all' });

  const trackNewAccounts = React.useCallback(
    (accounts: LinkedFinancialAccounts, isReconnection: boolean) => {
      accounts.forEach((account) => {
        trackAccountConnected({
          accountId: account.id,
          accountType: account.type,
          accountSubtype: account.subtype ?? null,
          bankName: account.bank.name,
          bankId: account.bank.id,
          isFirstAccount: financialAccounts.length === 0,
          isReconnection,
        });
      });
      if (!isReconnection) {
        incrementPeopleProperty('account_count', accounts.length);
      }
    },
    [financialAccounts.length]
  );

  const onCreateLinkToken = React.useCallback(
    async (connectionId: string | null | undefined) => {
      try {
        setIsLoading(true);
        setErrorCode(null);
        const response = await createLinkToken({
          variables: { input: { bankConnectionId: connectionId ?? undefined } },
        });
        if (response.errors) {
          logger.error('Failed to create link token');
          openModalWithError(extractErrorCode(response.errors));
          return;
        }
        const token = response.data?.createLinkToken?.linkToken;
        if (!token) {
          logger.error('Failed to create link token');
          openModalWithError(ApiErrorCode.PLAID_LINK_TOKEN_FAILED);
          return;
        }
        isMultiItemSessionRef.current = !connectionId;
        linkTokenRef.current = token;
        setLinkToken(token);
      } catch (err) {
        logger.error('Error occurred while creating link token', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
      }
    },
    [createLinkToken, openModalWithError]
  );

  const onAddAccounts = React.useCallback(
    async (metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        setIsLoading(true);
        setErrorCode(null);
        const institutionId = metadata.institution?.institution_id;
        if (!institutionId) {
          logger.error('Missing institution information');
          openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
          return;
        }
        const accounts = metadata.accounts.map((account) => ({
          id: account.id,
          name: account.name,
          mask: account.mask,
          type: account.type,
          subtype: account.subtype,
        }));

        // Link ran against this connection's token, so name it: the server's
        // institution lookup cannot tell two connections at one bank apart.
        const response = await createFinancialAccounts({
          variables: { input: { institutionId, accounts, bankConnectionId: selectedBankConnectionId ?? undefined } },
        });
        if (response.errors) {
          logger.error('Failed to create financial accounts');
          openModalWithError(extractErrorCode(response.errors));
          return;
        }
        if (!response.data?.createFinancialAccounts) {
          logger.error('Failed to create financial accounts');
          openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
          return;
        }
        trackNewAccounts(response.data.createFinancialAccounts, true);
        if (selectedBankConnectionId) {
          try {
            await archiveBankConnectionWorkItems({ variables: { bankConnectionId: selectedBankConnectionId } });
          } catch (err) {
            // Don't fail the reconnection if the archive fails.
            logger.error('Failed to archive bank connection work items', err);
          }
        }
        onLinkedRef.current?.(response.data.createFinancialAccounts, { isReconnection: true });
      } catch (err) {
        logger.error('Error occurred while adding accounts', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
      } finally {
        reset();
      }
    },
    [
      selectedBankConnectionId,
      createFinancialAccounts,
      archiveBankConnectionWorkItems,
      openModalWithError,
      reset,
      trackNewAccounts,
    ]
  );

  /**
   * Waits for the server's record of what a session became. The subscription
   * carries it the moment the webhook is handled and is matched on Plaid's
   * session id; the query underneath reads the row by our link token and is
   * the floor, since delivery is at-most-once. Resolves null when nothing
   * terminal arrives in time or the hook has unmounted.
   */
  const waitForLinkSession = React.useCallback(
    (linkToken: string, linkSessionId: string | null, timeoutMs: number): Promise<ILinkSessionWait> =>
      new Promise((resolve) => {
        let settled = false;
        let lastPollFailed = false;
        const isTerminal = (session: PlaidLinkTokenFieldsFragment | null | undefined) =>
          session?.status === PlaidLinkTokenStatus.Completed || session?.status === PlaidLinkTokenStatus.Failed;
        const finish = (session: PlaidLinkTokenFieldsFragment | null) => {
          if (settled) {
            return;
          }
          settled = true;
          subscription?.unsubscribe();
          resolve({ session, pollFailed: session === null && lastPollFailed });
        };

        const subscription = subscriptionsEnabled
          ? client.subscribe<PlaidLinkTokenUpdatedSubscription>({ query: PLAID_LINK_TOKEN_UPDATED }).subscribe({
              next: ({ data }) => {
                const session = data?.plaidLinkTokenUpdated;
                if (
                  session &&
                  linkSessionId !== null &&
                  session.linkSessionId === linkSessionId &&
                  isTerminal(session)
                ) {
                  finish(session);
                }
              },
              error: (err: unknown) => {
                logger.warn(`Link session subscription error: ${String(err)}`);
              },
            })
          : null;

        const deadline = Date.now() + timeoutMs;
        void (async () => {
          while (!settled && Date.now() < deadline && isMountedRef.current) {
            await sleep(LINK_SESSION_POLL_MS);
            if (settled) {
              return;
            }
            try {
              const { data } = await client.query<PlaidLinkTokenQuery, PlaidLinkTokenQueryVariables>({
                query: PLAID_LINK_TOKEN,
                variables: { linkToken },
                fetchPolicy: 'network-only',
              });
              lastPollFailed = false;
              const session = data.plaidLinkToken ?? null;
              if (session && isTerminal(session)) {
                finish(session);
                return;
              }
            } catch (err) {
              lastPollFailed = true;
              logger.warn(`Link session poll failed: ${String(err)}`);
            }
          }
          finish(null);
        })();
      }),
    [client, subscriptionsEnabled]
  );

  /**
   * Resolves a multi-item session from its recorded outcome. `onNothing`
   * decides what no record means: still pending after a completed session, or
   * a plain cancel after an exit. A record with nothing linked and nothing
   * wrong — the user backed out before any bank — is a cancel either way.
   *
   * A session that linked some banks and failed others keeps both halves: the
   * accounts that arrived are handed to `onLinked`, and the failure still
   * surfaces as `PLAID_EXCHANGE_FAILED`.
   */
  const awaitLinkSession = React.useCallback(
    async (linkSessionId: string | null, timeoutMs: number, onNothing: 'pending' | 'canceled') => {
      setIsLoading(true);
      setErrorCode(null);
      const known = new Set(financialAccountsRef.current.map((account) => account.id));
      const linkToken = linkTokenRef.current;
      const { session, pollFailed } = linkToken
        ? await waitForLinkSession(linkToken, linkSessionId, timeoutMs)
        : { session: null, pollFailed: false };
      if (!isMountedRef.current) {
        return;
      }

      const outcomes = session?.results.map((result) => result.outcome) ?? [];
      // A spent token is a server-side exchange failure, not a bank the user declined.
      const hasFailure =
        session?.status === PlaidLinkTokenStatus.Failed ||
        outcomes.includes(PlaidLinkTokenItemOutcome.Failed) ||
        outcomes.includes(PlaidLinkTokenItemOutcome.Spent);

      if (outcomes.includes(PlaidLinkTokenItemOutcome.Linked)) {
        let arrived: LinkedFinancialAccounts = [];
        try {
          const { data } = await refetchFinancialAccounts();
          arrived = (data?.getFinancialAccountsForUser ?? []).filter((account) => !known.has(account.id));
        } catch (err) {
          // The banks did link; we just cannot read them back, so the caller
          // must not be handed an empty list as a success.
          logger.error('Failed to read the accounts a Link session added', err);
          openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
          return;
        }
        trackNewAccounts(arrived, false);
        if (hasFailure) {
          onLinkedRef.current?.(arrived, { isReconnection: false });
          openModalWithError(ApiErrorCode.PLAID_EXCHANGE_FAILED);
          return;
        }
        reset();
        onLinkedRef.current?.(arrived, { isReconnection: false });
        return;
      }
      if (hasFailure) {
        openModalWithError(ApiErrorCode.PLAID_EXCHANGE_FAILED);
        return;
      }
      if (outcomes.length > 0 && outcomes.every((outcome) => outcome === PlaidLinkTokenItemOutcome.Duplicate)) {
        openModalWithError(ApiErrorCode.DUPLICATE_ITEM);
        return;
      }
      if (pollFailed) {
        // Reads kept failing, so the session may well have a terminal record —
        // reporting it as pending or canceled would be a guess.
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
        return;
      }
      if (session === null && onNothing === 'pending') {
        openModalWithError(ApiErrorCode.PLAID_LINK_PENDING);
        return;
      }
      reset();
      onCancelledRef.current?.();
    },
    [waitForLinkSession, refetchFinancialAccounts, openModalWithError, reset, trackNewAccounts]
  );

  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess: (_publicToken, metadata) => {
      if (isMultiItemSessionRef.current) {
        void awaitLinkSession(metadata.link_session_id ?? null, LINK_SESSION_WAIT_MS, 'pending');
        return;
      }
      onAddAccounts(metadata).catch(() => {
        // Error handling is done in onAddAccounts.
      });
    },
    onExit: (err, metadata) => {
      trackPlaidLinkExited({ hadError: !!err });
      if (err) {
        logger.error('Link was exited with an error', err);
        openModalWithError(ApiErrorCode.INTERNAL_SERVER_ERROR);
        return;
      }
      if (isMultiItemSessionRef.current) {
        void awaitLinkSession(metadata?.link_session_id ?? null, LINK_SESSION_EXIT_WAIT_MS, 'canceled');
        return;
      }
      // User backed out of Plaid without an error and without linking.
      reset();
      onCancelledRef.current?.();
    },
  };

  const { open, ready, exit } = usePlaidLink(config);

  // Auto-open Plaid once a link token is ready.
  React.useEffect(() => {
    if (linkToken && ready) {
      trackPlaidLinkOpened();
      open();
    }
  }, [linkToken, ready, open]);

  // Kick off token creation when a selection (or a skip-modal trigger) arms the flow.
  React.useEffect(() => {
    if (shouldTriggerPlaid && !linkToken && !isLoading) {
      onCreateLinkToken(selectedBankConnectionId).catch(() => {
        // Error handling is done in onCreateLinkToken.
      });
    }
  }, [shouldTriggerPlaid, linkToken, isLoading, selectedBankConnectionId, onCreateLinkToken]);

  // Close any open Plaid modal if the flow unmounts mid-link (e.g. the assistant
  // turn was cancelled). `exit` identity changes across renders, so call the
  // latest via a ref in a cleanup that runs exactly once on unmount.
  const exitRef = React.useRef(exit);
  exitRef.current = exit;
  // Set on every mount, not just initialized: StrictMode runs this cleanup
  // once on mount in dev, and a flag that only ever goes false would leave the
  // wait below returning before its first poll.
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      exitRef.current();
    };
  }, []);

  const trigger = React.useCallback(() => {
    if (shouldSkipModal) {
      // Restore the reconnection target (a prior error/exit may have reset it).
      setSelectedBankConnectionId(bankConnectionId);
      setShouldTriggerPlaid(true);
    } else {
      setIsModalOpen(true);
    }
  }, [shouldSkipModal, bankConnectionId]);

  // Distinguishes a modal close caused by a selection from a real user
  // dismissal — both arrive as onOpenChange(false). onModalSelect sets this
  // flag; onModalOpenChange reads it on the NEXT microtask, so it's correct
  // regardless of whether the modal fires onSelect before or after
  // onOpenChange (both run in the same synchronous tick, before the microtask).
  // Only a genuine dismissal leaves the flag false → onCancelled.
  const advancingRef = React.useRef(false);

  const onModalSelect = React.useCallback((connectionId: string | undefined) => {
    advancingRef.current = true;
    setSelectedBankConnectionId(connectionId);
    setShouldTriggerPlaid(true);
  }, []);

  const onModalOpenChange = React.useCallback((nextIsOpen: boolean) => {
    setIsModalOpen(nextIsOpen);
    if (!nextIsOpen) {
      setErrorCode(null);
      queueMicrotask(() => {
        if (advancingRef.current) {
          advancingRef.current = false;
          return;
        }
        onCancelledRef.current?.();
      });
    }
  }, []);

  return { trigger, shouldSkipModal, isModalOpen, onModalOpenChange, onModalSelect, errorCode, isLoading };
}
